import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      let top = 0;
      let left = 0;

      // We use viewport-relative coordinates because of position: fixed
      switch (position) {
        case 'bottom':
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
        case 'top':
        default:
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
      }
      setCoords({ top, left });
    }
  };

  useLayoutEffect(() => {
    if (isVisible) {
      updateCoords();
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('resize', updateCoords);
      window.addEventListener('scroll', updateCoords, true);
    }
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isVisible]);

  const getTransform = () => {
    switch (position) {
      case 'bottom': return 'translateX(-50%)';
      case 'left': return 'translateX(-100%) translateY(-50%)';
      case 'right': return 'translateY(-50%)';
      case 'top':
      default: return 'translateX(-50%) translateY(-100%)';
    }
  };

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-flex items-center shrink-0 cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </div>
      {createPortal(
        <AnimatePresence>
          {isVisible && content && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: position === 'top' ? 5 : -5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: position === 'top' ? 5 : -5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="fixed z-[99999] pointer-events-none"
              style={{ 
                top: coords.top, 
                left: coords.left,
                transform: getTransform()
              }}
            >
              <div className="relative w-max max-w-[280px] px-4 py-3 rounded-2xl bg-slate-900/95 backdrop-blur-2xl border border-white/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)]">
                <div className="text-[11px] text-slate-100 leading-relaxed font-semibold tracking-wide text-left flex flex-col gap-1">
                  {typeof content === 'string' ? content.split('\n').map((line, i) => (
                    <div key={i} className="">{line}</div>
                  )) : content}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
